import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, ExternalLink, Award, Lock, Pin, Phone, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/SEO";

const STEP_ICONS = {
  profile_complete: Sparkles,
  first_listing: ExternalLink,
  contact_regions: Phone,
  tier_reviewed: Award,
  onboarding_call_booked: Phone,
};

export default function OnboardingPage() {
  useEffect(() => { document.title = "Supplier Onboarding · Accommodation Suppliers"; }, []);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // step_id being marked

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=/onboarding"); return; }
    if (!["advertiser", "admin"].includes(user.role)) {
      toast.error("Onboarding is for supplier accounts only.");
      navigate("/");
      return;
    }
    load();
    // eslint-disable-next-line
  }, [user, authLoading]);

  const load = async () => {
    try {
      const { data } = await api.get("/onboarding/status");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load onboarding status");
    }
  };

  const markDone = async (stepId) => {
    setBusy(stepId);
    try {
      await api.post("/onboarding/mark", { step_id: stepId });
      await load();
      toast.success("Nice — step marked complete.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark step");
    } finally { setBusy(null); }
  };

  if (!data) {
    return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-[#7D8C85]" data-testid="onboarding-loading">Loading…</div>;
  }

  const { steps, progress, founding_supplier, founding_featured_until, founding_price_locked_until, founding_price, tier_label } = data;
  const complete = progress.complete;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="onboarding-page">
      <SEO title="Supplier onboarding" description="Five short steps to make sure buyers find you, trust you, and reach you fast on Accommodation Suppliers." path="/onboarding" noindex />
      <div className="tracking-overline mb-3 text-[#B35C4A]">Supplier onboarding</div>
      <h1 className="font-serif text-4xl md:text-5xl text-[#11211A] tracking-tight mb-3">
        {complete ? "You're live" : "Get your listing live"}
      </h1>
      <p className="text-[#4A5550] text-lg leading-relaxed max-w-2xl">
        {complete
          ? "Every step is done. Your listing is polished, discoverable, and ready to catch buyer enquiries."
          : "Five short steps to make sure buyers find you, trust you, and reach you fast."}
      </p>

      {/* Founding Supplier context card */}
      {founding_supplier && (
        <div className="mt-8 border-2 border-[#B8860B]/40 bg-gradient-to-br from-[#FFFBEB] to-[#F0EFEA] p-5 sm:p-6" data-testid="founding-context-card">
          <div className="flex items-center gap-2 text-[#B35C4A] mb-3">
            <Award className="w-5 h-5" />
            <div className="tracking-overline text-[11px]">You're a Founding Supplier</div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="flex items-center gap-1.5 text-[#264839]"><Lock className="w-3.5 h-3.5" /><span className="text-[10px] uppercase tracking-widest font-semibold">Locked price</span></div>
              <div className="mt-1 text-[#11211A]">{founding_price ? `$${founding_price}/mo` : tier_label} · until {(founding_price_locked_until || "").split("T")[0] || "—"}</div>
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-[#264839]"><Pin className="w-3.5 h-3.5" /><span className="text-[10px] uppercase tracking-widest font-semibold">Pinned placement</span></div>
              <div className="mt-1 text-[#11211A]">Category top-of-page until {(founding_featured_until || "").split("T")[0] || "—"}</div>
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-[#264839]"><Award className="w-3.5 h-3.5" /><span className="text-[10px] uppercase tracking-widest font-semibold">Public badge</span></div>
              <div className="mt-1 text-[#11211A]">Live on every listing</div>
            </div>
          </div>
        </div>
      )}

      {/* Progress */}
      <div className="mt-8" data-testid="onboarding-progress">
        <div className="flex items-baseline justify-between text-sm">
          <div className="tracking-overline text-[#264839]">Progress</div>
          <div className="text-[#4A5550]">{progress.done} of {progress.total} · {progress.percent}%</div>
        </div>
        <div className="mt-2 h-2 bg-[#E8ECE9] overflow-hidden">
          <div
            className={`h-full transition-all ${complete ? "bg-[#264839]" : "bg-[#B35C4A]"}`}
            style={{ width: `${progress.percent}%` }}
            data-testid="onboarding-progress-bar"
          />
        </div>
      </div>

      {/* Complete state */}
      {complete && (
        <div className="mt-8 bg-[#264839] text-white px-6 py-6 flex items-start gap-4" data-testid="onboarding-complete-banner">
          <CheckCircle2 className="w-8 h-8 flex-shrink-0" />
          <div>
            <div className="tracking-overline text-[10px] text-[#F0EFEA] opacity-80 mb-1">All done</div>
            <div className="font-serif text-2xl">You're live!</div>
            <p className="text-sm text-[#F0EFEA] opacity-90 mt-2 leading-relaxed">
              Your listing is complete, contactable, and ready for enquiries. Buyers can find you in the directory today.
            </p>
            <div className="mt-4 flex gap-2 flex-wrap">
              <Link to="/dashboard" className="btn-secondary" data-testid="onboarding-go-dashboard">Go to dashboard</Link>
              <Link to="/search" target="_blank" className="text-[10px] uppercase tracking-widest font-semibold px-4 py-2 border border-white/40 text-white hover:bg-white/10 transition" data-testid="onboarding-view-directory">View directory</Link>
            </div>
          </div>
        </div>
      )}

      {/* Checklist */}
      <ol className="mt-8 space-y-3" data-testid="onboarding-checklist">
        {steps.map((s, idx) => {
          const Icon = STEP_ICONS[s.id] || Circle;
          return (
            <li
              key={s.id}
              className={`bg-white border transition ${s.done ? "border-[#264839] bg-[#F5F7F5]" : "border-[#E8ECE9] hover:border-[#264839]/40"} p-5 flex items-start gap-4`}
              data-testid={`onboarding-step-${s.id}`}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${s.done ? "bg-[#264839] text-white" : "bg-[#F0EFEA] text-[#264839] border border-[#C4CFC9]"}`}>
                {s.done ? <CheckCircle2 className="w-5 h-5" /> : <span className="font-serif text-sm">{idx + 1}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${s.done ? "text-[#264839]" : "text-[#7D8C85]"}`} />
                      <h3 className={`font-semibold text-[15px] ${s.done ? "text-[#264839]" : "text-[#11211A]"}`}>{s.title}</h3>
                    </div>
                    <p className="text-xs text-[#4A5550] mt-1 leading-relaxed">{s.description}</p>
                  </div>
                  {s.done && <span className="text-[10px] uppercase tracking-widest font-semibold text-[#264839] flex-shrink-0" data-testid={`onboarding-step-${s.id}-done`}>Complete</span>}
                </div>
                {!s.done && (
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {s.id === "profile_complete" && (
                      <Link to="/dashboard" className="btn-secondary text-xs" data-testid={`onboarding-cta-${s.id}`}>Edit profile</Link>
                    )}
                    {s.id === "first_listing" && (
                      <Link to="/dashboard?new_listing=1" className="btn-secondary text-xs" data-testid={`onboarding-cta-${s.id}`}>Create listing</Link>
                    )}
                    {s.id === "contact_regions" && (
                      <Link to="/dashboard" className="btn-secondary text-xs" data-testid={`onboarding-cta-${s.id}`}>Add regions</Link>
                    )}
                    {s.id === "tier_reviewed" && (
                      <>
                        <Link to="/pricing" target="_blank" className="btn-secondary text-xs" data-testid={`onboarding-cta-${s.id}-review`}>View tiers</Link>
                        <button
                          type="button"
                          onClick={() => markDone(s.id)}
                          disabled={busy === s.id}
                          className="btn-primary text-xs disabled:opacity-50"
                          data-testid={`onboarding-cta-${s.id}`}
                        >
                          {busy === s.id ? "Saving…" : "Mark as reviewed"}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Iter68 · Standalone Onboarding Call status — NOT counted in the percentage. */}
      {data.onboarding_call && (
        <div
          className={`mt-6 bg-white border-l-4 ${data.onboarding_call.booked ? "border-[#264839]" : "border-[#B35C4A]"} p-5`}
          data-testid="onboarding-call-status"
        >
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${data.onboarding_call.booked ? "bg-[#264839] text-white" : "bg-[#F0EFEA] text-[#B35C4A] border border-[#B35C4A]/40"}`}>
              {data.onboarding_call.booked ? <CheckCircle2 className="w-5 h-5" /> : <Phone className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                  <div className="tracking-overline text-[10px] text-[#7D8C85]">Optional · doesn't affect your setup %</div>
                  <h3 className="font-semibold text-[15px] text-[#11211A] mt-0.5">{data.onboarding_call.title}</h3>
                </div>
                {data.onboarding_call.booked && (
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-[#264839]" data-testid="onboarding-call-status-booked">
                    Booked{data.onboarding_call.booked_at ? ` · ${(data.onboarding_call.booked_at + "").slice(0, 10)}` : ""}
                  </span>
                )}
              </div>
              <p className="text-xs text-[#4A5550] mt-1 leading-relaxed">{data.onboarding_call.description}</p>
              {!data.onboarding_call.booked && (
                <div className="mt-3 flex gap-2 flex-wrap">
                  <Link to={data.onboarding_call.action_url || "/contact?subject=Onboarding+call"} className="btn-secondary text-xs" data-testid="onboarding-call-cta-book">Book call</Link>
                  <button
                    type="button"
                    onClick={() => markDone("onboarding_call_booked")}
                    disabled={busy === "onboarding_call_booked"}
                    className="btn-primary text-xs disabled:opacity-50"
                    data-testid="onboarding-call-cta-mark"
                  >
                    {busy === "onboarding_call_booked" ? "Saving…" : "Mark as booked"}
                  </button>
                </div>
              )}
              {data.onboarding_call.booked && data.onboarding_call.join_url && (
                <a href={data.onboarding_call.join_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-[#264839] underline" data-testid="onboarding-call-join-url">
                  Join meeting ↗
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 pt-6 border-t border-[#E8ECE9] text-xs text-[#7D8C85]">
        Need help with any step? Email <a href="mailto:hello@accommodationsuppliers.com" className="underline hover:text-[#264839]">hello@accommodationsuppliers.com</a> or use our{" "}
        <Link to="/contact" className="underline hover:text-[#264839]">contact form</Link>.
      </div>
    </div>
  );
}
